import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLoading } from '../hooks/useLoading';
import { saveOvaturesDonor } from '../services/ovaturesService';

// Hard-coded dropdown options from production site (https://apply-now.ovatures.com/)
const states = [
  { id: 1, description: 'Alabama' }, { id: 2, description: 'Alaska' }, { id: 3, description: 'Arizona' },
  { id: 4, description: 'Arkansas' }, { id: 5, description: 'California' }, { id: 6, description: 'Colorado' },
  { id: 7, description: 'Connecticut' }, { id: 8, description: 'Delaware' }, { id: 9, description: 'Florida' },
  { id: 10, description: 'Georgia' }, { id: 11, description: 'Hawaii' }, { id: 12, description: 'Idaho' },
  { id: 13, description: 'Illinois' }, { id: 14, description: 'Indiana' }, { id: 15, description: 'Iowa' },
  { id: 16, description: 'Kansas' }, { id: 17, description: 'Kentucky' }, { id: 18, description: 'Louisiana' },
  { id: 19, description: 'Maine' }, { id: 20, description: 'Maryland' }, { id: 21, description: 'Massachusetts' },
  { id: 22, description: 'Michigan' }, { id: 23, description: 'Minnesota' }, { id: 24, description: 'Mississippi' },
  { id: 25, description: 'Missouri' }, { id: 26, description: 'Montana' }, { id: 27, description: 'Nebraska' },
  { id: 28, description: 'Nevada' }, { id: 29, description: 'New Hampshire' }, { id: 30, description: 'New Jersey' },
  { id: 31, description: 'New Mexico' }, { id: 32, description: 'New York' }, { id: 33, description: 'North Carolina' },
  { id: 34, description: 'North Dakota' }, { id: 35, description: 'Ohio' }, { id: 36, description: 'Oklahoma' },
  { id: 37, description: 'Oregon' }, { id: 38, description: 'Pennsylvania' }, { id: 39, description: 'Rhode Island' },
  { id: 40, description: 'South Carolina' }, { id: 41, description: 'South Dakota' }, { id: 42, description: 'Tennessee' },
  { id: 43, description: 'Texas' }, { id: 44, description: 'Utah' }, { id: 45, description: 'Vermont' },
  { id: 46, description: 'Virginia' }, { id: 47, description: 'Washington' }, { id: 48, description: 'West Virginia' },
  { id: 49, description: 'Wisconsin' }, { id: 50, description: 'Wyoming' }
];

const contactModes = [
  { id: 1, name: 'Email' },
  { id: 2, name: 'Phone call' },
  { id: 3, name: 'Text message' }
];

const races = [
  { id: 4, name: 'American Indian or Alaska Native' },  
  { id: 7, name: 'Asian' }, 
  { id: 1, name: 'Black or African American' },
  { id: 6, name: 'Native Hawaiian or Pacific Islander' }, 
  { id: 2, name: 'White' },
  { id: 9, name: 'Another Race' },
  { id: 12, name: 'Prefer not to answer' }
];

const educationLevels = [
  { id: 1, name: 'High School' },
  { id: 2, name: 'Trade School' },
  { id: 3, name: 'College' },
  { id: 4, name: 'Graduate School' },
  { id: 5, name: 'Post-Graduate School' }
];

const eyeColors = [
  { id: 1, name: 'Blue' },
  { id: 2, name: 'Green' },
  { id: 3, name: 'Brown' },
  { id: 4, name: 'Hazel' },
  { id: 5, name: 'Gray' },
  { id: 6, name: 'Amber' }
];

const hairColors = [
  { id: 1, name: 'Black' },
  { id: 2, name: 'Brown' },
  { id: 3, name: 'Blonde' },
  { id: 4, name: 'Red' },
  { id: 5, name: 'Auburn' },
  { id: 6, name: 'Gray' }
];

export default function WantToDonate() {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { register, handleSubmit, watch, formState: { errors, isSubmitted }, setValue } = useForm();
  const [photoBase64, setPhotoBase64] = useState('');
  const [policyAcceptTime, setPolicyAcceptTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [notification, setNotification] = useState(null);
  const notificationTimer = useRef(null);
  const formTopRef = useRef(null);

  const showNotification = useCallback((message, type = 'danger') => {
    if (notificationTimer.current) clearTimeout(notificationTimer.current);
    setNotification({ message, type });
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    notificationTimer.current = setTimeout(() => setNotification(null), 8000);
  }, []);

  const dismissNotification = () => setNotification(null);

  // Watch conditional fields
  const medicationsFlg = watch('medicationsFlg');
  const chronicMedsFlg = watch('chronicMedsFlg');
  const familyCancerFlg = watch('familyCancerFlg');
  const familyHealthFlg = watch('familyHealthFlg');
  const race = watch('race');

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setPhotoError('File size must be less than 5MB.');
        setPhotoBase64('');
        e.target.value = '';
        return;
      }
      setPhotoError('');
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        const content = dataUrl.substring(dataUrl.indexOf(',') + 1);
        setPhotoBase64(content);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePolicyChange = (e) => {
    setPolicyAcceptTime(e.target.checked ? new Date().toISOString() : null);
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e, onChange) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('phoneNumber', formatted, { shouldValidate: true });
    onChange(e);
  };

  // Destructure register for fields needing merged onChange handlers
  const { onChange: onPhoneChange, ...phoneRest } = register('phoneNumber', { required: true });
  const { onChange: onPrivacyChange, ...privacyRest } = register('acceptPrivacy', { required: true });

  const validateDOB = (value) => {
    if (!value) return 'Date of birth is required';

    const dob = new Date(value);
    const today = new Date();
    const age = Math.floor((today - dob) / (365.25 * 24 * 60 * 60 * 1000));

    if (age < 18) return 'You must be at least 18 years old';
    if (age > 31) return 'You must be 31 years old or younger';

    return true;
  };

  const onSubmit = async (data) => {
    if (submitting) return;

    if (!photoBase64) {
      setPhotoError('Please upload a photo.');
      return;
    }

    if (!executeRecaptcha) {
      showNotification('reCAPTCHA not ready. Please try again.');
      return;
    }

    setSubmitting(true);
    startLoading();

    try {
      const recaptchaToken = await executeRecaptcha('submit_donor_application');

      if (!recaptchaToken) {
        showNotification('reCAPTCHA verification failed. Please try again.');
        return;
      }

      // Format the data to match backend expectations
      const donorData = {
        location: data.location,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: new Date(data.dateOfBirth + "T09:00:00").toISOString(),
        city: data.city,
        state: states.find(s => s.id === parseInt(data.state)),
        email: { emailAddress: data.email },
        phone: {
          phonePrefix: data.phonePrefix || '+1',
          phoneNumber: data.phoneNumber,
          phoneExtension: data.phoneExtension || ''
        },
        contactMode: contactModes.find(c => c.id === parseInt(data.contactMode)),
        photo: photoBase64,
        eyeColor: eyeColors.find(e => e.id === parseInt(data.eyeColor)),
        hairColor: hairColors.find(h => h.id === parseInt(data.hairColor)),
        heightFt: parseInt(data.heightFt),
        heightInch: parseInt(data.heightInch),
        weight: parseInt(data.weight),
        race: races.find(r => r.id === parseInt(data.race)),
        educationLevel: educationLevels.find(e => e.id === parseInt(data.educationLevel)),
        smokerFlg: data.smokerFlg === 'true',
        medicationsFlg: data.medicationsFlg === 'true',
        medications: data.medications || '',
        chronicMedsFlg: data.chronicMedsFlg === 'true',
        chronicMeds: data.chronicMeds || '',
        familyCancerFlg: data.familyCancerFlg === 'true',
        familyCancer: data.familyCancer || '',
        familyHealthFlg: data.familyHealthFlg === 'true',
        familyHealth: data.familyHealth || '',
        raceText: data.raceText || '',
        policyAcceptTime: policyAcceptTime,
        latestStatus: { id: 1 },
        recaptchaToken: recaptchaToken
      };

      const response = await saveOvaturesDonor(donorData);

      if (response.data.ovaturesDonor) {
        if (response.data.message) {
          const msg = response.data.message;
          if (msg.indexOf('matching name and date of birth') > -1) {
            navigate('/thank-you', { state: { warning: 'An existing application was found with matching name and date of birth.' } });
            return;
          } else if (msg.indexOf('Application flagged for coordinator review due to potential duplicate records.') > -1) {
            navigate('/thank-you', { state: { warning: 'An application with this email/phone already exists.' } });
            return;
          }
        }
        navigate('/thank-you');
      } else {
        showNotification('Error in submitting donor application.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      showNotification('Error in submitting donor application.');
    } finally {
      setSubmitting(false);
      stopLoading();
    }
  };

  return (
    <div id="wanttodonate" className="content">
      <div className="page-banner"></div>
      <div className="page-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="page-title">Want to donate?</h1>
              <div ref={formTopRef} />
              {notification && (
                <div className={`alert alert-${notification.type} alert-dismissible fade show`} role="alert">
                  {notification.message}
                  <button type="button" className="close" onClick={dismissNotification} aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}
              <form onSubmit={handleSubmit(onSubmit)} noValidate>

                {/* Location */}
                <div className="form-group row">
                  <div className="col-lg-12">
                    <label>Location *</label>
                    <select className="form-control" {...register('location', { required: true })}>
                      <option value="">Select the center you would like to donate at</option>
                      <option value="NJ">Ovatures New Jersey (RMA New Jersey)</option>                      
                      <option value="CO">Ovatures Colorado (Conceptions Reproductive Associates of Colorado)</option>
                    </select>
                    {errors.location && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* First Name / Last Name */}
                <div className="form-group row">
                  <div className="col-lg-6 md-mb16">
                    <label>First Name *</label>
                    <input type="text" className="form-control text-capitalize" {...register('firstName', { required: true })} />
                    {errors.firstName && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-6">
                    <label>Last Name *</label>
                    <input type="text" className="form-control text-capitalize" {...register('lastName', { required: true })} />
                    {errors.lastName && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Date of Birth / City */}
                <div className="form-group row">
                  <div className="col-lg-6 md-mb16">
                    <label>Date of Birth *</label>
                    <div className="row">
                      <div className="col-12">
                        <input
                          type="date"
                          className="form-control"
                          {...register('dateOfBirth', {
                            required: true,
                            validate: validateDOB
                          })}
                        />
                        {errors.dateOfBirth && <div className="invalid-feedback d-block">{errors.dateOfBirth.message || 'This field is required.'}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <label>City *</label>
                    <input type="text" className="form-control" {...register('city', { required: true })} />
                    {errors.city && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* State / Email */}
                <div className="form-group row">
                  <div className="col-lg-6 md-mb16">
                    <label>State *</label>
                    <select className="form-control" {...register('state', { required: true })}>
                      <option value=""></option>
                      {states.map(state => (
                        <option key={state.id} value={state.id}>{state.description}</option>
                      ))}
                    </select>
                    {errors.state && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-6">
                    <label>E-mail *</label>
                    <input type="email" className="form-control" {...register('email', { required: true, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email address.' } })} />
                    {errors.email && <div className="invalid-feedback d-block">{errors.email.message || 'This field is required.'}</div>}
                  </div>
                </div>

                {/* Phone / Contact Mode */}
                <div className="form-group row">
                  <div className="col-lg-6 md-mb16">
                    <label>Telephone number *</label>
                    <div className="row">
                      <div className="col-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="+1"
                          maxLength="4"
                          {...register('phonePrefix')}
                        />
                      </div>
                      <div className="col-6 pl-0">
                        <input
                          type="text"
                          className="form-control"
                          maxLength="13"
                          {...phoneRest}
                          onChange={(e) => handlePhoneChange(e, onPhoneChange)}
                        />
                        {errors.phoneNumber && <div className="invalid-feedback d-block">This field is required.</div>}
                      </div>
                      <div className="col-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="ext"
                          {...register('phoneExtension')}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <label>Preferred communication method *</label>
                    <select className="form-control" {...register('contactMode', { required: true })}>
                      <option value=""></option>
                      {contactModes.map(mode => (
                        <option key={mode.id} value={mode.id}>{mode.name}</option>
                      ))}
                    </select>
                    {errors.contactMode && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                <h2 className="color-DB006A mb30">A few more questions...</h2>

                {/* Photo */}
                <div className="form-group row">
                  <div className="col-lg-12">
                    <label>Photo *</label>
                    <input
                      id="file"
                      type="file"
                      className="d-block"
                      accept=".jpg,.png,.jpeg"
                      onChange={handleFileChange}
                    />
                    {photoError && <div className="invalid-feedback d-block" style={{ fontSize: '100%' }}>{photoError}</div>}
                    {isSubmitted && !photoBase64 && !photoError && <div className="invalid-feedback d-block" style={{ fontSize: '100%' }}>This field is required.</div>}
                  </div>
                </div>

                {/* Eye Color / Hair Color */}
                <div className="form-group row">
                  <div className="col-lg-6 md-mb16">
                    <label>Eye Color *</label>
                    <select className="form-control" {...register('eyeColor', { required: true })}>
                      <option value=""></option>
                      {eyeColors.map(color => (
                        <option key={color.id} value={color.id}>{color.name}</option>
                      ))}
                    </select>
                    {errors.eyeColor && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-6">
                    <label>Hair Color *</label>
                    <select className="form-control" {...register('hairColor', { required: true })}>
                      <option value=""></option>
                      {hairColors.map(color => (
                        <option key={color.id} value={color.id}>{color.name}</option>
                      ))}
                    </select>
                    {errors.hairColor && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Height / Weight / Ethnicity */}
                <div className="form-group row">
                  <div className="col-lg-4 md-mb16">
                    <div className="row">
                      <div className="col-md-12">
                        <label>Height *</label>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6 md-mb16 suffix-ft">
                        <select className="form-control" {...register('heightFt', { required: true })}>
                          <option value="">---</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                          <option value="6">6</option>
                          <option value="7">7</option>
                        </select>
                        {errors.heightFt && <div className="invalid-feedback d-block">This field is required.</div>}
                      </div>
                      <div className="col-6 suffix-in">
                        <select className="form-control" {...register('heightInch', { required: true })}>
                          <option value="">---</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={i} value={i}>{i}</option>
                          ))}
                        </select>
                        {errors.heightInch && <div className="invalid-feedback d-block">This field is required.</div>}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4 md-mb16">
                    <label>Weight *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="pounds"
                      {...register('weight', { required: true, pattern: { value: /^\d+$/, message: 'Please enter a valid number.' } })}
                    />
                    {errors.weight && <div className="invalid-feedback d-block">{errors.weight.message || 'This field is required.'}</div>}
                  </div>
                </div>         
                
                {/* Race */}
                <div className="form-group row">
                  <div className="col-lg-4">
                    <label>What is your race? *</label>
                    <select className="form-control"  {...register('race', { required: true })}>
                      <option value=""></option>
                      {races.map(race => (
                        <option key={race.id} value={race.id}>{race.name}</option>
                      ))}
                    </select>
                    {errors.race && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-4">
                    <label>if another race, please specify</label>
                    <textarea
                      rows="1"
                      className="form-control"
                       {...register('raceText', { required: race === '9' })}
                    />
                    {errors.raceText && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>
                
                {/* Education Level */}
                 <div className="form-group row">
                  <div className="col-lg-4">
                    <label>Highest level of education *</label>
                    <select className="form-control" {...register('educationLevel', { required: true })}>
                      <option value=""></option>
                      {educationLevels.map(level => (
                        <option key={level.id} value={level.id}>{level.name}</option>
                      ))}
                    </select>
                    {errors.educationLevel && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>
               

                {/* Smoker */}
                <div className="form-group row">
                  <div className="col-lg-12">
                    <label>Do you smoke? *</label>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="true" {...register('smokerFlg', { required: true })} />
                        Yes
                      </label>
                    </div>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="false" {...register('smokerFlg', { required: true })} />
                        No
                      </label>
                    </div>
                    {errors.smokerFlg && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Medications */}
                <div className="form-group row">
                  <div className="col-lg-7 md-mb16">
                    <label>Do you take any medications? *</label>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="true" {...register('medicationsFlg', { required: true })} />
                        Yes
                      </label>
                    </div>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="false" {...register('medicationsFlg', { required: true })} />
                        No
                      </label>
                    </div>
                    {errors.medicationsFlg && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-5">
                    <label>if yes, please specify</label>
                    <textarea
                      rows="2"
                      className="form-control"
                      {...register('medications', { required: medicationsFlg === 'true' })}
                    />
                    {errors.medications && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Chronic Medical Conditions */}
                <div className="form-group row">
                  <div className="col-lg-7 md-mb16">
                    <label>Do you have any chronic medical conditions? *</label>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="true" {...register('chronicMedsFlg', { required: true })} />
                        Yes
                      </label>
                    </div>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="false" {...register('chronicMedsFlg', { required: true })} />
                        No
                      </label>
                    </div>
                    {errors.chronicMedsFlg && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-5">
                    <label>if yes, please specify</label>
                    <textarea
                      rows="2"
                      className="form-control"
                      {...register('chronicMeds', { required: chronicMedsFlg === 'true' })}
                    />
                    {errors.chronicMeds && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Family Cancer */}
                <div className="form-group row">
                  <div className="col-lg-7 md-mb16">
                    <label>Do you have any family members with cancer? *</label>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="true" {...register('familyCancerFlg', { required: true })} />
                        Yes
                      </label>
                    </div>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="false" {...register('familyCancerFlg', { required: true })} />
                        No
                      </label>
                    </div>
                    {errors.familyCancerFlg && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-5">
                    <label>if yes, please specify</label>
                    <textarea
                      rows="2"
                      className="form-control"
                      {...register('familyCancer', { required: familyCancerFlg === 'true' })}
                    />
                    {errors.familyCancer && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Family Mental Health */}
                <div className="form-group row">
                  <div className="col-lg-7 md-mb16">
                    <label>Do you have any family members with a serious mental health condition? *</label>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="true" {...register('familyHealthFlg', { required: true })} />
                        Yes
                      </label>
                    </div>
                    <div className="form-check-inline">
                      <label className="form-check-label">
                        <input type="radio" className="form-check-input" value="false" {...register('familyHealthFlg', { required: true })} />
                        No
                      </label>
                    </div>
                    {errors.familyHealthFlg && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                  <div className="col-lg-5">
                    <label>if yes, please specify</label>
                    <textarea
                      rows="2"
                      className="form-control"
                      {...register('familyHealth', { required: familyHealthFlg === 'true' })}
                    />
                    {errors.familyHealth && <div className="invalid-feedback d-block">This field is required.</div>}
                  </div>
                </div>

                {/* Privacy Policy */}
                <div className="form-group row">
                  <div className="col-lg-12">
                    <div className="form-check">
                      <label className="form-check-label">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          {...privacyRest}
                          onChange={(e) => { onPrivacyChange(e); handlePolicyChange(e); }}
                        />
                        I have read and accept the <a href="/docs/NOTICE-OF-PRIVACY-PRACTICES_9.23.13.pdf" target="_blank" rel="noopener noreferrer" className="color-DB006A">privacy policy</a>
                        {errors.acceptPrivacy && <div className="invalid-feedback d-block">This field is required.</div>}
                      </label>
                    </div>
                  </div>
                </div>

                {/* reCAPTCHA v3 (invisible) - configured in App.jsx with GoogleReCaptchaProvider */}

                {/* Submit */}
                <div className="form-group row">
                  <div className="col-lg-12">
                    <input type="submit" value="Submit Application" className="btn btn-ovatures" disabled={submitting} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}